package com.example.mystybag.core.expected

import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.Font
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import dev.icerock.moko.resources.ColorResource
import dev.icerock.moko.resources.FontResource
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.provider.JsStringProvider
import dev.icerock.moko.resources.provider.RemoteJsStringLoader
import com.example.mystybag.common.resources.SR
import com.example.mystybag.sharedmodels.SCImage
import kotlinx.browser.window
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.datetime.LocalDate
import kotlin.coroutines.resume
import kotlin.js.Date

/** Cached provider from RemoteJsStringLoader.getOrLoad(); set before first UI so sharedString returns translated text. */
var sharedStringProvider: JsStringProvider? = null

fun setSharedStringProvider(provider: JsStringProvider?) {
    sharedStringProvider = provider
}

/** Call before showing UI (e.g. in main()). Loads JSON strings so sharedString() returns translations. */
suspend fun initSharedStringProvider() {
    // IMPORTANT: access the loader through the typed `RemoteJsStringLoaderHolder.stringsLoader`
    // member, NOT via `asDynamic()`/`loader_1`. The dynamic lookup depends on the minified
    // property name, which the Kotlin/JS IR compiler renames in production webpack builds —
    // that made `sharedStringProvider` stay null on staging/prod and all sharedString() texts
    // render blank, while moko `stringResource()` (own loader) kept working.
    try {
        val loader: RemoteJsStringLoader = SR.strings.stringsLoader
        setSharedStringProvider(loader.getOrLoad())
    } catch (e: Throwable) {
        console.error("[Strings][Web] initSharedStringProvider FAILED: ${e.message}")
        console.error(e)
    }
}

actual fun getVersion(): String = "1"
actual fun getVersionName(): String = "1.0"
actual fun getBuildModel(): String = "Web"
actual fun getDeviceName(): String = "Web Browser"
// Web is always served in Bulgarian, regardless of any saved preference or browser locale.
actual fun getLanguage(): String = "bg"
actual fun getDisplayLanguage(locale: String): String = locale
actual fun getOSVersionCode(): String = "0"
actual fun sharedString(res: StringResource, vararg args: Any): String {
    val provider = sharedStringProvider
    val locale = getLanguage()
    if (provider != null) {
        return if (args.isEmpty()) {
            res.localized(provider = provider, locale = locale)
        } else {
            res.localized(provider = provider, locale = locale, *args)
        }
    }
    // Fallback before init: return key so UI at least shows something
    val dyn = res.asDynamic()
    val key = (dyn.key_1 ?: dyn.key ?: dyn["key_1"] ?: dyn["key"]) as? String ?: return ""
    return key
}
actual fun sharedColor(res: ColorResource): Color = Color.Unspecified
actual fun getFont(fontName: String, fontResource: FontResource, weight: FontWeight, style: FontStyle): Font = Font(0, weight, style)
actual fun openSettings() {}
actual fun openNotificationsSettings() {}
actual fun getUserLocation(activity: Activity) {}
actual fun goToPhoneApp(phoneNumber: String) {}
actual fun isLocationEnabled(): Boolean = false
actual fun isDeviceSecured(): Boolean = false
actual fun openAppStorePage() {}
actual fun getAppCachePath(): String = ""
actual fun getDeviceUUID(): String = "web-${window.performance.now().toLong()}"
actual fun openInExternalBrowser(link: String) { window.open(link, "_blank") }
actual fun getPackageName(): String = "com.example.mystybag.web"
actual fun launchEmailApp() {}
actual fun SCImage.getSize(): Size = try {
    val skiaImage = org.jetbrains.skia.Image.makeFromEncoded(this.data)
    Size(skiaImage.width.toFloat(), skiaImage.height.toFloat())
} catch (_: Throwable) {
    Size(this.data.size.toFloat(), 1f)
}
actual fun getAppLabel(): String = "MystyBag"

actual fun decodeImage(data: ByteArray): SCImage? = SCImage(data)
actual fun deleteFileByFilePath(filePath: String) {}
actual fun deleteFileByFileName(fileName: String) {}
actual fun createFont(fontResource: FontResource, weight: FontWeight): Font = Font(0, weight, FontStyle.Normal)
actual fun copyToClipboard(text: String) { /* use Clipboard API in browser if needed */ }
actual fun shareText(text: String) {}
actual fun sendEmailToSupportWithBodyExtra(emailSubject: String, email: String) {}
actual fun setScreenBrightness(isFull: Boolean) {}
actual fun isNFCChipPresent(): Boolean = false
actual fun openDevicePasswordSettings() {}
actual fun setScreenshotRestriction(shouldSet: Boolean) {}
actual suspend fun getLocateFromAddress(searchBy: String): Pair<Double, Double>? = null
actual suspend fun getAddressFromLocation(latitude: Double, longitude: Double): String? = null
actual suspend fun getCurrentUserLocation(): Pair<Double, Double>? = null
// sessionStorage keys for the mobile full-page Google OAuth redirect flow.
private const val KEY_GOOGLE_OAUTH_STATE = "mb_g_state"
private const val KEY_GOOGLE_OAUTH_PENDING = "mb_g_pending"

private fun encodeURIComponentJs(value: String): String =
    js("encodeURIComponent").unsafeCast<(String) -> String>()(value)

private fun decodeURIComponentJs(value: String): String =
    try { js("decodeURIComponent").unsafeCast<(String) -> String>()(value) } catch (_: Throwable) { value }

private fun sessionGet(key: String): String? =
    try { window.sessionStorage.getItem(key) } catch (_: Throwable) { null }

private fun sessionSet(key: String, value: String) {
    try { window.sessionStorage.setItem(key, value) } catch (_: Throwable) { }
}

private fun sessionRemove(key: String) {
    try { window.sessionStorage.removeItem(key) } catch (_: Throwable) { }
}

/** Heuristic: are we on a mobile/touch browser where OAuth popups become new tabs? */
private fun isMobileWeb(): Boolean = try {
    val nav = window.navigator.asDynamic()
    val ua = (nav.userAgent as? String).orEmpty()
    val uaMobile = Regex(
        "Android|iPhone|iPad|iPod|Mobile|Silk|Kindle|BlackBerry|Opera Mini|IEMobile",
        RegexOption.IGNORE_CASE
    ).containsMatchIn(ua)
    val maxTouch = (nav.maxTouchPoints as? Int) ?: 0
    val coarse = try {
        (window.asDynamic().matchMedia("(pointer: coarse)").matches as? Boolean) ?: false
    } catch (_: Throwable) { false }
    // iPadOS reports a desktop UA but exposes touch points + a coarse pointer.
    uaMobile || (maxTouch > 0 && coarse)
} catch (_: Throwable) { false }

/** Cryptographically-random hex token for OAuth `state`/`nonce`. */
private fun randomToken(): String = try {
    val arr = js("new Uint8Array(16)")
    window.asDynamic().crypto.getRandomValues(arr)
    val sb = StringBuilder()
    val len = (arr.length as? Int) ?: 0
    for (i in 0 until len) {
        val b = (arr[i] as Int) and 0xff
        sb.append(b.toString(16).padStart(2, '0'))
    }
    sb.toString()
} catch (_: Throwable) {
    "f" + window.performance.now().toLong().toString(16) + (0..0xFFFF).random().toString(16)
}

/** Drop the URL fragment (e.g. `#access_token=...`) without reloading, so we never loop. */
private fun cleanUrlFragment() {
    try {
        val loc = window.location
        window.history.replaceState(null, "", loc.pathname + loc.search)
    } catch (_: Throwable) {
        try { window.location.hash = "" } catch (_: Throwable) { }
    }
}

private fun parseFragmentParams(fragment: String): Map<String, String> {
    val out = mutableMapOf<String, String>()
    for (pair in fragment.split("&")) {
        if (pair.isEmpty()) continue
        val idx = pair.indexOf('=')
        if (idx < 0) {
            out[decodeURIComponentJs(pair)] = ""
        } else {
            out[decodeURIComponentJs(pair.substring(0, idx))] = decodeURIComponentJs(pair.substring(idx + 1))
        }
    }
    return out
}

/** Outcome of returning from the Google redirect. `accessToken` set on success; `error` otherwise. */
private class GoogleRedirectOutcome(val accessToken: String?, val error: String?)

/**
 * If we just came back from the full-page Google redirect, parse and validate the URL
 * fragment. Always clears the pending flag and cleans the URL so a refresh cannot re-trigger.
 * Returns null when there is no pending redirect.
 */
private fun consumeGoogleRedirectResult(): GoogleRedirectOutcome? {
    if (sessionGet(KEY_GOOGLE_OAUTH_PENDING) != "1") return null
    val savedState = sessionGet(KEY_GOOGLE_OAUTH_STATE)
    val fragment = window.location.hash.removePrefix("#")
    // Clear state up-front regardless of outcome to avoid loops.
    sessionRemove(KEY_GOOGLE_OAUTH_PENDING)
    sessionRemove(KEY_GOOGLE_OAUTH_STATE)
    cleanUrlFragment()

    if (fragment.isEmpty()) return GoogleRedirectOutcome(null, "no_fragment")
    val params = parseFragmentParams(fragment)

    val err = params["error"]
    if (!err.isNullOrEmpty()) {
        console.warn("[GoogleSignIn][Web] redirect returned error='$err'")
        return GoogleRedirectOutcome(null, err)
    }
    val token = params["access_token"]
    if (token.isNullOrEmpty()) return GoogleRedirectOutcome(null, "no_token")
    val returnedState = params["state"]
    if (savedState == null || savedState != returnedState) {
        console.error("[GoogleSignIn][Web] redirect state mismatch (possible CSRF); ignoring token")
        return GoogleRedirectOutcome(null, "state_mismatch")
    }
    return GoogleRedirectOutcome(token, null)
}

/** Start the mobile full-page OAuth 2.0 implicit redirect to Google. The page unloads after this. */
private fun startGoogleRedirect(clientId: String) {
    val state = randomToken()
    sessionSet(KEY_GOOGLE_OAUTH_STATE, state)
    sessionSet(KEY_GOOGLE_OAUTH_PENDING, "1")
    val redirectUri = window.location.origin + "/"
    // NOTE: no `nonce` here. `nonce` is only valid for OIDC id_token flows; Google rejects it
    // for the implicit access-token flow (`response_type=token`) with
    // "invalid_request: Parameter not allowed for this message type: nonce". `state` covers CSRF.
    val params = "client_id=" + encodeURIComponentJs(clientId) +
        "&redirect_uri=" + encodeURIComponentJs(redirectUri) +
        "&response_type=token" +
        "&scope=" + encodeURIComponentJs("openid email profile") +
        "&prompt=select_account" +
        "&include_granted_scopes=true" +
        "&state=" + encodeURIComponentJs(state)
    console.log("[GoogleSignIn][Web] mobile flow: redirecting to Google (redirect_uri=$redirectUri)")
    window.location.href = "https://accounts.google.com/o/oauth2/v2/auth?$params"
}

/** Exchange a Google OAuth access token for the user's profile via the UserInfo endpoint. */
private suspend fun fetchGoogleUserInfo(accessToken: String): GoogleSignInResult? =
    suspendCancellableCoroutine { cont ->
        var done = false
        fun finish(value: GoogleSignInResult?) {
            if (done) return
            done = true
            if (cont.isActive) cont.resume(value)
        }
        val headers: dynamic = js("({})")
        headers.Authorization = "Bearer $accessToken"
        val init: dynamic = js("({})")
        init.headers = headers
        window.asDynamic()
            .fetch("https://openidconnect.googleapis.com/v1/userinfo", init)
            .then { res: dynamic -> res.json() }
            .then { profile: dynamic ->
                try {
                    val email = (profile?.email as? String).orEmpty()
                    val firstName = profile?.given_name as? String
                    val lastName = profile?.family_name as? String
                    val photo = profile?.picture as? String
                    console.log(
                        "[GoogleSignIn][Web] userinfo: email='$email' " +
                            "firstName='${firstName ?: "<null>"}' " +
                            "lastName='${lastName ?: "<null>"}' " +
                            "photoUrl='${photo ?: "<null>"}'"
                    )
                    if (email.isEmpty()) {
                        console.error("[GoogleSignIn][Web] userinfo returned no email")
                        finish(null)
                    } else {
                        finish(
                            GoogleSignInResult(
                                email = email,
                                idToken = accessToken,
                                firstName = firstName?.takeIf { it.isNotBlank() },
                                lastName = lastName?.takeIf { it.isNotBlank() },
                                photoUrl = photo?.takeIf { it.isNotBlank() },
                            )
                        )
                    }
                } catch (e: Throwable) {
                    console.error("[GoogleSignIn][Web] userinfo parse failed: ${e.message}")
                    finish(null)
                }
                Unit
            }
            .catch { err: dynamic ->
                console.error("[GoogleSignIn][Web] userinfo fetch failed: ${err?.message}")
                finish(null)
                Unit
            }
    }

/**
 * Desktop Google Sign-In: OAuth 2.0 **token** popup flow
 * (`google.accounts.oauth2.initTokenClient` + `requestAccessToken()`).
 *
 * This opens a real popup window on the user's click (a real gesture), reliable regardless
 * of cookie/FedCM state, and exchanges the access token for the profile via UserInfo. It is
 * NOT used on mobile, where the popup becomes a new tab and the token can never post back to
 * the opener (see [signInWithGoogle], which uses a full-page redirect there instead).
 */
private suspend fun signInWithGooglePopup(clientId: String): GoogleSignInResult? = suspendCancellableCoroutine { cont ->
    val oauth2 = window.asDynamic().google?.accounts?.oauth2
    if (oauth2 == undefined || oauth2 == null) {
        console.error("[GoogleSignIn][Web] google.accounts.oauth2 is unavailable. Is the GIS script (https://accounts.google.com/gsi/client) loaded yet?")
        cont.resume(null)
        return@suspendCancellableCoroutine
    }

    var resolved = false
    fun resolveOnce(value: GoogleSignInResult?) {
        if (resolved) return
        resolved = true
        if (cont.isActive) cont.resume(value)
    }

    fun fetchUserInfo(accessToken: String) {
        val headers: dynamic = js("({})")
        headers.Authorization = "Bearer $accessToken"
        val init: dynamic = js("({})")
        init.headers = headers
        window.asDynamic()
            .fetch("https://openidconnect.googleapis.com/v1/userinfo", init)
            .then { res: dynamic -> res.json() }
            .then { profile: dynamic ->
                try {
                    val email = (profile?.email as? String).orEmpty()
                    val firstName = profile?.given_name as? String
                    val lastName = profile?.family_name as? String
                    val photo = profile?.picture as? String
                    console.log(
                        "[GoogleSignIn][Web] userinfo: email='$email' " +
                            "firstName='${firstName ?: "<null>"}' " +
                            "lastName='${lastName ?: "<null>"}' " +
                            "photoUrl='${photo ?: "<null>"}'"
                    )
                    if (email.isEmpty()) {
                        console.error("[GoogleSignIn][Web] userinfo returned no email")
                        resolveOnce(null)
                    } else {
                        resolveOnce(
                            GoogleSignInResult(
                                email = email,
                                idToken = accessToken,
                                firstName = firstName?.takeIf { it.isNotBlank() },
                                lastName = lastName?.takeIf { it.isNotBlank() },
                                photoUrl = photo?.takeIf { it.isNotBlank() },
                            )
                        )
                    }
                } catch (e: Throwable) {
                    console.error("[GoogleSignIn][Web] userinfo parse failed: ${e.message}")
                    resolveOnce(null)
                }
                Unit
            }
            .catch { err: dynamic ->
                console.error("[GoogleSignIn][Web] userinfo fetch failed: ${err?.message}")
                resolveOnce(null)
                Unit
            }
    }

    try {
        val cfg: dynamic = js("({})")
        cfg.client_id = clientId
        cfg.scope = "openid email profile"
        cfg.callback = { resp: dynamic ->
            try {
                val err = resp?.error as? String
                val accessToken = resp?.access_token as? String
                when {
                    !err.isNullOrEmpty() -> {
                        console.warn("[GoogleSignIn][Web] token error='$err'")
                        resolveOnce(null)
                    }
                    accessToken.isNullOrEmpty() -> {
                        console.warn("[GoogleSignIn][Web] no access_token in response")
                        resolveOnce(null)
                    }
                    else -> fetchUserInfo(accessToken)
                }
            } catch (e: Throwable) {
                console.error("[GoogleSignIn][Web] token callback failed: ${e.message}")
                resolveOnce(null)
            }
            Unit
        }
        // Fires when the popup is closed/blocked or another non-OAuth error occurs.
        cfg.error_callback = { errEvent: dynamic ->
            val type = (errEvent?.type as? String) ?: "<unknown>"
            console.warn(
                "[GoogleSignIn][Web] oauth2 error_callback type='$type'. " +
                    "If 'popup_failed_to_open', allow popups; if 'popup_closed', the user dismissed it. " +
                    "Also ensure the page origin is listed under Authorized JavaScript origins for client_id=$clientId."
            )
            resolveOnce(null)
            Unit
        }
        val tokenClient = oauth2.initTokenClient(cfg)
        tokenClient.requestAccessToken()
    } catch (e: Throwable) {
        console.error("[GoogleSignIn][Web] init/requestAccessToken failed: ${e.message}")
        resolveOnce(null)
    }

    cont.invokeOnCancellation { resolved = true }
}

actual fun hasPendingGoogleRedirect(): Boolean = try {
    sessionGet(KEY_GOOGLE_OAUTH_PENDING) == "1" &&
        (window.location.hash.contains("access_token=") || window.location.hash.contains("error="))
} catch (_: Throwable) { false }

/**
 * Google Sign-In on Web.
 *
 * Two flows, chosen by device:
 *  - **Mobile**: a full-page OAuth 2.0 implicit redirect to Google
 *    (`https://accounts.google.com/o/oauth2/v2/auth?response_type=token`). On return the
 *    access token arrives in the URL fragment, which [consumeGoogleRedirectResult] parses
 *    (after the Welcome screen re-triggers this via [hasPendingGoogleRedirect]). This avoids
 *    the popup token flow, whose popup becomes a NEW TAB on mobile and never posts the token
 *    back to `window.opener`, leaving the app hung.
 *  - **Desktop**: the GIS popup token flow (see [signInWithGooglePopup]).
 *
 * The backend only needs `email` + profile fields (no token is sent server-side), so the
 * access token (exchanged for the profile via UserInfo) is sufficient.
 *
 * Mobile requires the return URL (`<origin>/`) registered under "Authorized redirect URIs"
 * of the Web OAuth client in Google Cloud Console.
 */
actual suspend fun signInWithGoogle(): GoogleSignInResult? {
    val clientId = (window.asDynamic().MystyBagGoogleClientId as? String).orEmpty()
    if (clientId.isEmpty()) {
        console.error("[GoogleSignIn][Web] window.MystyBagGoogleClientId is not set in index.html")
        return null
    }

    // 1. Returning from the mobile full-page redirect?
    val outcome = consumeGoogleRedirectResult()
    if (outcome != null) {
        val token = outcome.accessToken
        console.log("[GLOG][Web] consumed redirect: tokenLen=${token?.length ?: 0} error='${outcome.error ?: "<none>"}'")
        return if (token.isNullOrEmpty()) null else fetchGoogleUserInfo(token)
    }

    // 2. Mobile web: start the full-page redirect. The page unloads, so this suspend never resumes.
    if (isMobileWeb()) {
        startGoogleRedirect(clientId)
        return suspendCancellableCoroutine { /* page navigates away */ }
    }

    // 3. Desktop web: popup token flow.
    return signInWithGooglePopup(clientId)
}
/** Decode a JWT payload (the base64url JSON between the dots) into a map of claims. */
private fun decodeJwtPayload(jwt: String): Map<String, Any?> {
    return try {
        val parts = jwt.split(".")
        if (parts.size < 2) return emptyMap()
        // Base64url -> Base64
        var b64 = parts[1].replace('-', '+').replace('_', '/')
        while (b64.length % 4 != 0) b64 += "="
        val json = js("atob").unsafeCast<(String) -> String>()(b64)
        val obj = js("JSON").parse(json)
        val out = mutableMapOf<String, Any?>()
        val keys = js("Object").keys(obj).unsafeCast<Array<String>>()
        for (k in keys) out[k] = obj[k]
        out
    } catch (e: Throwable) {
        console.warn("[JWT] decodeJwtPayload failed: ${e.message}")
        emptyMap()
    }
}

/**
 * Sign in with Apple on Web, using Apple's JS SDK (`appleid.auth.js`) in popup mode.
 *
 * Configuration (see also `index.html`):
 *  - `window.MystyBagAppleServicesId` — the Apple Services ID (e.g. `com.mystybag.web`),
 *    configured with "Sign In with Apple", the web domains under "Domains and Subdomains",
 *    and full "Return URLs". The page domain must also be VERIFIED in Apple
 *    (`/.well-known/apple-developer-domain-association.txt`) or `signIn()` is rejected.
 *  - `redirectURI` is computed at runtime as `${origin}/` so the same build works on
 *    staging and production — it only needs to MATCH a registered Return URL (popup mode).
 *
 * Flow:
 *  1. `AppleID.auth.init({ clientId, scope, redirectURI, usePopup: true })`.
 *  2. `AppleID.auth.signIn()` resolves with `{ authorization: { id_token, code }, user? }`.
 *     - `id_token` (JWT) is always present; we decode it for the `email` claim.
 *     - `user.name.{firstName,lastName}` + `user.email` are ONLY present on the FIRST
 *       sign-in for a given Services ID + user. On repeat sign-ins email comes from the
 *       id_token claim; name is unavailable (backend must resolve via the `sub` claim).
 *
 * Apple does NOT provide a profile photo URL — [AppleSignInResult] has no such field.
 */
actual suspend fun signInWithApple(): AppleSignInResult? = suspendCancellableCoroutine { cont ->
    val servicesId = (window.asDynamic().MystyBagAppleServicesId as? String).orEmpty()
    if (servicesId.isEmpty()) {
        console.error("[AppleSignIn][Web] window.MystyBagAppleServicesId is not set in index.html")
        cont.resume(null)
        return@suspendCancellableCoroutine
    }

    val appleId = window.asDynamic().AppleID
    if (appleId == undefined || appleId == null) {
        console.error("[AppleSignIn][Web] window.AppleID is unavailable. Is the Apple JS SDK (appleid.auth.js) loaded yet?")
        cont.resume(null)
        return@suspendCancellableCoroutine
    }

    // Popup mode only requires redirectURI to match a registered Return URL. Allow an
    // explicit override via window var, otherwise derive it from the current origin.
    val redirectUri = (window.asDynamic().MystyBagAppleRedirectUri as? String)
        ?.takeIf { it.isNotBlank() }
        ?: (window.location.origin + "/")

    var resolved = false
    fun resolveOnce(value: AppleSignInResult?) {
        if (resolved) return
        resolved = true
        if (cont.isActive) cont.resume(value)
    }

    try {
        val initCfg: dynamic = js("({})")
        initCfg.clientId = servicesId
        initCfg.scope = "name email"
        initCfg.redirectURI = redirectUri
        initCfg.usePopup = true
        appleId.auth.init(initCfg)

        appleId.auth.signIn()
            .then { resp: dynamic ->
                try {
                    val idToken = (resp?.authorization?.id_token as? String).orEmpty()
                    if (idToken.isEmpty()) {
                        console.error("[AppleSignIn][Web] no id_token in authorization response")
                        resolveOnce(null)
                    } else {
                        val payload = decodeJwtPayload(idToken)
                        val userObj = resp?.user
                        // Email: id_token claim is always present; user.email only on first sign-in.
                        val email = ((payload["email"] as? String) ?: (userObj?.email as? String)).orEmpty()
                        // Name: only on the first sign-in, inside resp.user.name.
                        val nameObj = userObj?.name
                        val firstName = nameObj?.firstName as? String
                        val lastName = nameObj?.lastName as? String
                        console.log(
                            "[AppleSignIn][Web] signIn ok: email='$email' " +
                                "firstName='${firstName ?: "<null>"}' " +
                                "lastName='${lastName ?: "<null>"}' " +
                                "idTokenLen=${idToken.length}"
                        )
                        if (email.isEmpty()) {
                            console.warn("[AppleSignIn][Web] empty email (likely a subsequent sign-in; backend must resolve via the id_token 'sub' claim)")
                        }
                        resolveOnce(
                            AppleSignInResult(
                                email = email,
                                idToken = idToken,
                                firstName = firstName?.takeIf { it.isNotBlank() },
                                lastName = lastName?.takeIf { it.isNotBlank() },
                            )
                        )
                    }
                } catch (e: Throwable) {
                    console.error("[AppleSignIn][Web] signIn parse failed: ${e.message}")
                    resolveOnce(null)
                }
                Unit
            }
            .catch { err: dynamic ->
                // Apple rejects with e.g. { error: 'popup_closed_by_user' | 'user_cancelled_authorize' }.
                val reason = (err?.error as? String) ?: "${err?.message ?: "<unknown>"}"
                console.warn(
                    "[AppleSignIn][Web] signIn cancelled/failed: '$reason'. " +
                        "If this is 'invalid_client' or similar, verify the Services ID '$servicesId', " +
                        "that redirectURI='$redirectUri' is a registered Return URL, and that the domain is verified."
                )
                resolveOnce(null)
                Unit
            }
    } catch (e: Throwable) {
        console.error("[AppleSignIn][Web] init/signIn failed: ${e.message}")
        resolveOnce(null)
    }

    cont.invokeOnCancellation { resolved = true }
}

/**
 * Facebook Sign-In on Web using the Facebook JS SDK.
 *
 * Prerequisites (see also `index.html`):
 *  - Facebook App created at developers.facebook.com.
 *  - `window.MystyBagFacebookAppId` set in `index.html` to your App ID.
 *  - In Facebook Login product settings: enable Web OAuth Login, add your origin
 *    (e.g. `localhost`, `mystybag.com`) to "Allowed Domains for the JavaScript SDK".
 *
 * Flow:
 *  1. Call `FB.login(...)` with `scope: "email,public_profile"`.
 *  2. On success, call `FB.api('/me', { fields: 'id,email,first_name,last_name,picture' })`
 *     to fetch profile fields the user agreed to share.
 *  3. Wrap into [FacebookSignInResult].
 */
actual suspend fun signInWithFacebook(): FacebookSignInResult? = suspendCancellableCoroutine { cont ->
    val appId = (window.asDynamic().MystyBagFacebookAppId as? String).orEmpty()
    if (appId.isEmpty() || appId.startsWith("XXXX")) {
        console.error("[FacebookSignIn][Web] window.MystyBagFacebookAppId is not configured in index.html")
        if (cont.isActive) cont.resume(null)
        return@suspendCancellableCoroutine
    }

    val fb = window.asDynamic().FB
    if (fb == undefined || fb == null) {
        console.error("[FacebookSignIn][Web] window.FB is unavailable. Is the JS SDK script loaded yet (https://connect.facebook.net/en_US/sdk.js)?")
        if (cont.isActive) cont.resume(null)
        return@suspendCancellableCoroutine
    }

    var resolved = false
    fun resolveOnce(value: FacebookSignInResult?) {
        if (resolved) return
        resolved = true
        if (cont.isActive) cont.resume(value)
    }

    try {
        val loginOptions: dynamic = js("({})")
        loginOptions.scope = "email,public_profile"
        loginOptions.return_scopes = true

        val onLogin: (dynamic) -> Unit = { resp ->
            try {
                val authResp = resp?.authResponse
                if (authResp == null || authResp == undefined) {
                    val status = (resp?.status as? String) ?: "<unknown>"
                    console.warn("[FacebookSignIn][Web] login cancelled or failed. status='$status'")
                    resolveOnce(null)
                } else {
                    val accessToken = (authResp.accessToken as? String).orEmpty()
                    val userId = (authResp.userID as? String).orEmpty()

                    val apiParams: dynamic = js("({})")
                    apiParams.fields = "id,email,first_name,last_name,picture.width(256).height(256)"

                    val onMe: (dynamic) -> Unit = { me ->
                        try {
                            if (me?.error != null && me.error != undefined) {
                                console.error("[FacebookSignIn][Web] /me error: ${me.error.message}")
                                resolveOnce(null)
                            } else {
                                val email = (me?.email as? String).orEmpty()
                                val firstName = me?.first_name as? String
                                val lastName = me?.last_name as? String
                                val photoUrl: String? = try {
                                    val pic = me?.picture?.data
                                    if (pic != null && pic != undefined && pic.is_silhouette == false) {
                                        pic.url as? String
                                    } else null
                                } catch (_: Throwable) {
                                    null
                                }

                                console.log(
                                    "[FacebookSignIn][Web] profile: email='$email' " +
                                        "userId='$userId' " +
                                        "firstName='${firstName ?: "<null>"}' " +
                                        "lastName='${lastName ?: "<null>"}' " +
                                        "photoUrl='${photoUrl ?: "<null>"}' " +
                                        "tokenLen=${accessToken.length}"
                                )

                                resolveOnce(
                                    FacebookSignInResult(
                                        email = email,
                                        userId = userId,
                                        accessToken = accessToken,
                                        firstName = firstName?.takeIf { it.isNotBlank() },
                                        lastName = lastName?.takeIf { it.isNotBlank() },
                                        photoUrl = photoUrl?.takeIf { it.isNotBlank() },
                                    )
                                )
                            }
                        } catch (e: Throwable) {
                            console.error("[FacebookSignIn][Web] /me callback failed: ${e.message}")
                            resolveOnce(null)
                        }
                    }

                    fb.api("/me", apiParams, onMe)
                }
            } catch (e: Throwable) {
                console.error("[FacebookSignIn][Web] login callback failed: ${e.message}")
                resolveOnce(null)
            }
        }

        fb.login(onLogin, loginOptions)
    } catch (e: Throwable) {
        console.error("[FacebookSignIn][Web] FB.login failed: ${e.message}")
        resolveOnce(null)
    }

    cont.invokeOnCancellation { resolved = true }
}
actual fun openMapForLocation(latitude: Double, longitude: Double, address: String) { window.open("https://www.google.com/maps?q=$latitude,$longitude", "_blank") }

/** Matches [String.format] usages in common code, e.g. `"%.2f"`, `"%.1f"` (JS had been returning raw [Double.toString]). */
private val jsDoubleDecimalFormat = Regex("^%\\.(\\d+)f$")

actual fun format(format: String, value: Double): String {
    val decimals = jsDoubleDecimalFormat.matchEntire(format)?.groupValues?.get(1)?.toIntOrNull()
        ?: return value.toString()
    if (decimals !in 0..21) return value.toString()
    return js("(function(v,dec){ return v.toFixed(dec); })")(value, decimals) as String
}

actual fun format(format: String, vararg args: Any): String = args.joinToString()

@Suppress("UNUSED_VARIABLE")
actual fun formatLocalMonthYear(date: LocalDate): String {
    val d = Date(date.year, date.monthNumber - 1, date.dayOfMonth)
    val options = js("({ month: 'long', year: 'numeric' })")
    return d.asDynamic().toLocaleDateString(getLanguage(), options) as String
}
actual fun byteArrayFromPath(uri: String): ByteArray? {
    if (!uri.startsWith("data:")) return null
    val base64 = uri.substringAfter(',')
    if (base64.isEmpty()) return null
    return try {
        val binary = js("atob").unsafeCast<(String) -> String>()(base64)
        val charCodeAt = js("(function(s,i){return s.charCodeAt(i);})").unsafeCast<(String, Int) -> Int>()
        ByteArray(binary.length) { i -> charCodeAt(binary, i).and(0xFF).toByte() }
    } catch (_: Throwable) {
        null
    }
}
actual fun byteArrayFromFile(filename: String): ByteArray? = null
actual fun scImageFromPath(uri: String): SCImage? = null
actual fun scImageFromFile(filename: String): SCImage? = null
actual fun SCImage.toByteArray(): ByteArray = this.data
actual fun turnScreenOn() {}
actual fun turnScreenOff() {}
actual fun openLargeMap(lat: Double, lon: Double, address: String) { window.open("https://www.google.com/maps?q=$lat,$lon", "_blank") }
actual fun loadImageFromURL(url: String?): SCImage? = null
actual fun setSoftInputModeOverlay(overlay: Boolean) {}
